import { AquariusButton } from "@smartrr/shared/customerPortal/components/AquariusButton";
import { typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import React, { useEffect, useState } from "react";

import {
  AquariusModal,
  IAquariusAdvancedModalProps,
} from "@customer-app/account/views/OperationAquarius/components/AquariusModal";

// Utils & Helpers

import { IModalOptions, openModal, useCloseModal } from "@customer-app/state/actionCreators/modal";
import {
  useSmartrrCustomerDispatch,
  useSmartrrCustomerSelector,
} from "@customer-app/state/typedCustomerReduxHooks";

import { getCaption, getMainTitle, getSubtitle } from "./utils";
import {
  HowToEarnPointsModal,
  HowToEarnPointsModalProps,
} from "../AquariusInner/components/Loyalty/components/modals/HowToEarnPointsModal";
import {
  RedeemVariantModal,
  RedeemVariantModalProps,
} from "../AquariusInner/components/Loyalty/components/modals/RedeemVariantModal";
import {
  IOrderHistoryProps,
  OrderDetailsModal,
} from "../AquariusInner/components/OrderHistory/OrderDetailsModal";
import { getModalBadges } from "../AquariusInner/components/OrderHistory/utils";
import { BirthdayModal, IBirthdayModal } from "../AquariusInner/components/Settings/BirthdayModal";
import { EmailModal, IEmailProps } from "../AquariusInner/components/Settings/EmailModal";
import { AddonList } from "../AquariusInner/components/Subscription/AddonList";
import { AddonListProps } from "../AquariusInner/components/Subscription/AddonList/libs/types";
import { EditBundleModal } from "../AquariusInner/components/Subscription/Modals/components/EditBundleModal";
import { IEditBundleModal } from "../AquariusInner/components/Subscription/Modals/components/EditBundleModal/libs/types";
import {
  IManageSubscriptionsModalProps,
  ManageSubscriptionsModal,
} from "../AquariusInner/components/Subscription/Modals/ManageSubscriptionsModal";
import {
  IOrderOptionsModalInnerProps,
  OrderOptionsModalInner,
} from "../AquariusInner/components/Subscription/Modals/OrderOptionsModalInner";
import { ProductDescriptionModal } from "../AquariusInner/components/Subscription/Modals/ProductDescriptionModal";
import { ProductDescriptionModalProps } from "../AquariusInner/components/Subscription/Modals/ProductDescriptionModal/libs/types";
import { SelectModalViewModal } from "../AquariusInner/components/Subscription/Modals/SelectModalViewModal";
import { ISelectModalViewProps } from "../AquariusInner/components/Subscription/Modals/SelectModalViewModal/types";
import {
  IUpcomingOrdersModalProps,
  UpcomingOrdersModal,
} from "../AquariusInner/components/Subscription/Modals/UpcomingOrdersModal";
import { useSubscriptionStoreInit } from "../AquariusInner/store/SubscriptionActionStore";
import { ErrorBoundary } from "../components/ErrorBoundary";
import { useOrganization } from "../utils";
import { DeliveryAccess } from "@customer-app/state/stores/delivery";
import {
  RedeemIncentiveModal,
  RedeemIncentiveModalProps,
} from "../AquariusInner/components/Loyalty/components/modals/RedeemIncentiveModal";

interface IOrderOptionProps extends IOrderOptionsModalInnerProps {
  showUpcomingOrderButton?: boolean;
}

export interface IModal {
  ORDER_OPTIONS: IOrderOptionProps;
  UPCOMING_ORDERS: IUpcomingOrdersModalProps;
  MANAGE_SUBSCRIPTIONS: IManageSubscriptionsModalProps;
  ORDER_HISTORY: IOrderHistoryProps;
  CHANGE_EMAIL: IEmailProps;
  REDEEM_PRODUCT: RedeemVariantModalProps;
  REDEEM_INCENTIVE: RedeemIncentiveModalProps;
  HOW_TO_EARN_POINTS: HowToEarnPointsModalProps;
  EDIT_BUNDLE: IEditBundleModal;
  CHANGE_BIRTHDAY: IBirthdayModal;
  SELECT_MODAL: ISelectModalViewProps;
  PRODUCT_DESCRIPTION: ProductDescriptionModalProps;
  ADDON_LIST: AddonListProps;
  CLOSE_MODAL: {};
}

const testIdMap: Record<ModalComponent, string> = {
  ORDER_OPTIONS: "order-options",
  UPCOMING_ORDERS: "upcoming-orders",
  MANAGE_SUBSCRIPTIONS: "manage-subs",
  ORDER_HISTORY: "order-history",
  CHANGE_EMAIL: "change-email",
  REDEEM_PRODUCT: "redeem-product",
  REDEEM_INCENTIVE: "redeem-incentive",
  HOW_TO_EARN_POINTS: "how-to-earn",
  EDIT_BUNDLE: "edit-bundle",
  CHANGE_BIRTHDAY: "change-bday",
  SELECT_MODAL: "select-modal",
  PRODUCT_DESCRIPTION: "product-desc",
  ADDON_LIST: "addon-list",
  CLOSE_MODAL: "close-modal",
};

export type ModalComponent = keyof IModal;
export type Modal<M extends ModalComponent> = IModal[M];

type DeliveryModals =
  | IManageSubscriptionsModalProps
  | IOrderOptionProps
  | IEditBundleModal
  | ISelectModalViewProps
  | ProductDescriptionModalProps
  | AddonListProps;

const getDeliveryIdFromProps = (compType: ModalComponent | null, props: IModal[ModalComponent] | null) => {
  if (!props || !compType) {
    return null;
  }

  if (
    compType === "MANAGE_SUBSCRIPTIONS" ||
    compType === "ORDER_OPTIONS" ||
    compType === "EDIT_BUNDLE" ||
    compType === "SELECT_MODAL" ||
    compType === "PRODUCT_DESCRIPTION" ||
    compType === "ADDON_LIST"
  ) {
    return (props as DeliveryModals)?.deliveryId ?? null;
  }
};

export function GlobalModal(): JSX.Element | null {
  const dispatch = useSmartrrCustomerDispatch();
  const closeModal = useCloseModal();
  const organization = useOrganization();
  const { activeComponent, activeComponentProps, options } = useSmartrrCustomerSelector(state => state.modal);
  // Need to account for changing between modal types from within a modal
  const [localActiveComponent, setLocalActiveComponent] = useState<ModalComponent | null>(activeComponent);
  const [localActiveComponentProps, setLocalActiveComponentProps] = useState<IModal[ModalComponent] | null>(
    activeComponentProps
  );

  const modalDelivery = DeliveryAccess.useDelivery(
    getDeliveryIdFromProps(localActiveComponent, localActiveComponentProps) ?? {
      cpsId: "",
      index: -1,
    }
  );

  // Ensuring that the delivery being passed to modal components is correct and not the same delivery used for init
  useSubscriptionStoreInit(modalDelivery?.purchaseState ?? undefined);

  useEffect(() => {
    if (activeComponent === null) {
      return;
    }
    setLocalActiveComponent(activeComponent);
    setLocalActiveComponentProps(activeComponentProps);
  }, [activeComponent, activeComponentProps]);

  if (!localActiveComponent || !dispatch) {
    return null;
  }

  const modalProps: Partial<IAquariusAdvancedModalProps> = {
    mainTitle: getMainTitle(localActiveComponent, localActiveComponentProps!, modalDelivery),
    subTitle: getSubtitle(localActiveComponent, localActiveComponentProps!, modalDelivery, organization!),
    caption: getCaption(
      localActiveComponent,
      localActiveComponentProps!,
      modalDelivery,
      (component: ModalComponent, props?: IModal[ModalComponent], options?: IModalOptions) =>
        dispatch(openModal(component, props, options))
    ),
    ...(localActiveComponent === "MANAGE_SUBSCRIPTIONS" && {
      status: modalDelivery?.purchaseState.purchaseStateStatus ?? undefined,
    }),
    ...(localActiveComponent === "ORDER_HISTORY" && {
      statusBadges: getModalBadges((localActiveComponentProps as IModal["ORDER_HISTORY"])?.order),
    }),
    contained: options?.contained,
  };

  const generateModalContent = () => {
    switch (localActiveComponent) {
      case "ORDER_OPTIONS": {
        return (
          <React.Fragment>
            <OrderOptionsModalInner {...(localActiveComponentProps as IModal["ORDER_OPTIONS"])} />
            {(localActiveComponentProps as IModal["ORDER_OPTIONS"]).showUpcomingOrderButton ? (
              <AquariusButton onClick={() => dispatch(openModal("UPCOMING_ORDERS", {}))}>
                {typedLocalize("general.backToUpcomingOrdersBtn")}
              </AquariusButton>
            ) : null}
          </React.Fragment>
        );
      }
      case "MANAGE_SUBSCRIPTIONS": {
        return <ManageSubscriptionsModal {...(localActiveComponentProps as IModal["MANAGE_SUBSCRIPTIONS"])} />;
      }
      case "UPCOMING_ORDERS": {
        return <UpcomingOrdersModal {...(localActiveComponentProps as IModal["UPCOMING_ORDERS"])} />;
      }
      case "ORDER_HISTORY": {
        return <OrderDetailsModal {...(localActiveComponentProps as IModal["ORDER_HISTORY"])} />;
      }
      case "CHANGE_EMAIL": {
        return <EmailModal {...(localActiveComponentProps as IModal["CHANGE_EMAIL"])} />;
      }
      case "REDEEM_INCENTIVE": {
        return <RedeemIncentiveModal {...(localActiveComponentProps as IModal["REDEEM_INCENTIVE"])} />;
      }
      case "REDEEM_PRODUCT": {
        return <RedeemVariantModal {...(localActiveComponentProps as IModal["REDEEM_PRODUCT"])} />;
      }
      case "HOW_TO_EARN_POINTS": {
        return <HowToEarnPointsModal {...(localActiveComponentProps as IModal["HOW_TO_EARN_POINTS"])} />;
      }
      case "EDIT_BUNDLE": {
        return <EditBundleModal {...(localActiveComponentProps as IModal["EDIT_BUNDLE"])} />;
      }
      case "CHANGE_BIRTHDAY": {
        return <BirthdayModal {...(localActiveComponentProps as IModal["CHANGE_BIRTHDAY"])} />;
      }
      case "SELECT_MODAL": {
        return <SelectModalViewModal {...(localActiveComponentProps as IModal["SELECT_MODAL"])} />;
      }
      case "ADDON_LIST": {
        return <AddonList {...(localActiveComponentProps as IModal["ADDON_LIST"])} />;
      }
      case "PRODUCT_DESCRIPTION": {
        return <ProductDescriptionModal {...(localActiveComponentProps as IModal["PRODUCT_DESCRIPTION"])} />;
      }
      case "CLOSE_MODAL":
      default: {
        return <React.Fragment />;
      }
    }
  };

  const modalContent = generateModalContent();

  const isOpen =
    activeComponent !== null && activeComponent !== "CLOSE_MODAL" && localActiveComponentProps !== null;

  return (
    <ErrorBoundary useModalState onReset={closeModal}>
      <AquariusModal
        open={isOpen}
        onClose={closeModal}
        modalDescriptor={testIdMap[localActiveComponent]}
        {...modalProps}
      >
        {modalContent}
      </AquariusModal>
    </ErrorBoundary>
  );
}
